/**
 * Tenant 식별 유틸리티 (Learning Center B2B 전용)
 *
 * 개발 환경: 서브도메인 기반으로 tenant 식별
 *   - seoul101.localhost:3100 → seoul101
 *   - busan101.localhost:3100 → busan101
 *   - localhost:3100 (서브도메인 없음) → 랜딩 페이지
 *
 * 프로덕션 환경: 도메인에서 tenant 추출
 *   - seoul101.quebonlc.tv → seoul101
 *   - quebonlc.tv (서브도메인 없음) → 랜딩 페이지
 *
 * 스테이징 환경: staging 서브도메인 포함
 *   - seoul101.staging.quebonlc.tv → seoul101
 *   - staging.quebonlc.tv (서브도메인 없음) → 랜딩 페이지
 *
 * 도메인 구조:
 * - LC B2B: *.quebonlc.tv (프로덕션), *.staging.quebonlc.tv (스테이징)
 *
 * @see docs/troubleshooting/tenant-identification.md
 */

/**
 * 도메인별 조직 타입 설정
 * Learning Center 전용 앱이므로 LC 도메인만 포함
 */
export const DOMAIN_CONFIG: Record<string, { type: string; baseDomain: string }> = {
    "quebonlc.tv": { type: "learning_center", baseDomain: "quebonlc.tv" },
    "staging.quebonlc.tv": { type: "learning_center", baseDomain: "staging.quebonlc.tv" },
};

/**
 * 알려진 base 도메인 목록 (길이 긴 것부터 = longest match first)
 * staging 등 multi-level 도메인을 올바르게 감지하기 위해 사용
 */
const KNOWN_BASE_DOMAINS = ["staging.quebonlc.tv", "quebonlc.tv"];

/**
 * 공용 fallback 로고
 * Super Admin에서 organizations.logo_url을 직접 등록하므로, 테넌트별 매핑 대신
 * 미설정 시 공용 Quebon 로고 하나만 fallback으로 사용한다.
 */
export const DEFAULT_TENANT_LOGO = "/quebon-logo.svg";

/**
 * 테넌트 로고 URL 가져오기
 * 1순위: API에서 받은 logoUrl
 * 2순위: DEFAULT_TENANT_LOGO (공용 Quebon 로고)
 *
 * tenantId 인자는 호출 측 호환을 위해 유지되지만 현재는 사용하지 않는다.
 */
export const getTenantLogoUrl = (_tenantId: string | null, apiLogoUrl: string | null | undefined): string => {
    if (apiLogoUrl) {
        return apiLogoUrl;
    }
    return DEFAULT_TENANT_LOGO;
};

/**
 * 예약된 서브도메인 (tenant로 사용 불가)
 */
const RESERVED_SUBDOMAINS = ["www", "b2b", "admin", "api", "app", "lc"];

/**
 * 로컬 개발 환경인지 확인
 * ⚠️ import.meta.env.PROD 대신 이 함수 사용 권장
 */
export const isLocalDevelopment = (): boolean => {
    const hostname = window.location.hostname;
    return (
        hostname === "localhost" ||
        hostname.endsWith(".localhost") ||
        hostname.startsWith("127.") ||
        hostname.startsWith("192.168.") ||
        hostname.endsWith(".local") ||
        hostname.endsWith(".nip.io")
    );
};

/**
 * 호스트네임에서 베이스 도메인 추출
 * 예: seoul101.quebonlc.tv → quebonlc.tv
 *     seoul101.staging.quebonlc.tv → staging.quebonlc.tv
 *     staging.quebonlc.tv → staging.quebonlc.tv
 *     seoul101.localhost → localhost
 *     seoul101.192.168.1.5.nip.io → 192.168.1.5.nip.io
 */
export const getBaseDomain = (hostname: string): string | null => {
    // localhost 서브도메인 처리
    if (hostname.endsWith(".localhost")) {
        return "localhost";
    }
    if (hostname === "localhost") {
        return "localhost";
    }

    // nip.io 처리 (LAN 접속: seoul101.192.168.1.5.nip.io → 192.168.1.5.nip.io)
    if (hostname.endsWith(".nip.io")) {
        const match = hostname.match(/(\d+\.\d+\.\d+\.\d+\.nip\.io)$/);
        return match ? match[1] : null;
    }

    // 알려진 multi-level base 도메인 우선 매칭 (longest match first)
    for (const domain of KNOWN_BASE_DOMAINS) {
        if (hostname === domain || hostname.endsWith("." + domain)) {
            return domain;
        }
    }

    // Fallback: 마지막 2개 부분
    const parts = hostname.split(".");
    if (parts.length >= 2) {
        return parts.slice(-2).join(".");
    }
    return null;
};

/**
 * 호스트네임에서 서브도메인 추출
 * base 도메인을 감지하여 그 앞부분을 서브도메인으로 반환
 * 예: seoul101.quebonlc.tv → seoul101
 *     seoul101.staging.quebonlc.tv → seoul101
 *     staging.quebonlc.tv → null (base 도메인 자체)
 *     seoul101.localhost → seoul101
 *     localhost → null (서브도메인 없음)
 */
export const getSubdomain = (hostname: string): string | null => {
    // localhost 서브도메인 처리 (예: seoul101.localhost)
    if (hostname.endsWith(".localhost")) {
        const subdomain = hostname.replace(".localhost", "");
        return subdomain || null;
    }

    // localhost만 있으면 서브도메인 없음
    if (hostname === "localhost") {
        return null;
    }

    // nip.io 서브도메인 처리 (예: seoul101.192.168.1.5.nip.io → seoul101)
    if (hostname.endsWith(".nip.io")) {
        const match = hostname.match(/^(.+?)\.(\d+\.\d+\.\d+\.\d+\.nip\.io)$/);
        return match ? match[1] : null;
    }

    // base 도메인 감지 후, 그 앞부분을 서브도메인으로 추출
    const baseDomain = getBaseDomain(hostname);
    if (baseDomain && hostname !== baseDomain) {
        const subdomain = hostname.slice(0, -(baseDomain.length + 1));
        return subdomain || null;
    }

    return null;
};

/**
 * 현재 도메인의 조직 타입 가져오기
 * Learning Center 전용 앱이므로 항상 "learning_center" 반환
 */
export const getOrganizationType = (): string => {
    return "learning_center";
};

/**
 * 랜딩 페이지인지 확인
 * 서브도메인이 없거나 예약된 서브도메인이면 랜딩 페이지
 */
export const isLandingPage = (): boolean => {
    const hostname = window.location.hostname;
    const subdomain = getSubdomain(hostname);
    return subdomain === null || RESERVED_SUBDOMAINS.includes(subdomain);
};

/**
 * 현재 tenant ID(subdomain) 가져오기
 * 서브도메인 기반 멀티테넌시
 * 
 * 우선순위:
 * 1. 서브도메인에서 추출 (seoul101.localhost:3100 → seoul101)
 * 2. 쿼리파라미터 (?tenant=xxx) - 개발/테스트용
 * 3. localStorage - 세션 유지용
 * 
 * 서브도메인이 없으면 null 반환 (랜딩 페이지)
 */
export const getTenantId = (): string | null => {
    const hostname = window.location.hostname;
    
    // 1. 서브도메인에서 추출
    const subdomain = getSubdomain(hostname);
    
    if (subdomain && !RESERVED_SUBDOMAINS.includes(subdomain)) {
        localStorage.setItem("tenant", subdomain);
        console.log(`[Tenant] Subdomain tenant: ${subdomain}`);
        return subdomain;
    }
    
    // 2. 쿼리파라미터 확인 (개발/테스트용)
    const params = new URLSearchParams(window.location.search);
    const paramTenant = params.get("tenant");
    if (paramTenant) {
        localStorage.setItem("tenant", paramTenant);
        console.log(`[Tenant] Tenant from query param: ${paramTenant}`);
        return paramTenant;
    }
    
    // 3. 서브도메인 없음 = 랜딩 페이지
    // localStorage 값은 사용하지 않음 (랜딩에서 특정 테넌트로 자동 이동 방지)
    console.log(`[Tenant] No subdomain - landing page mode`);
    return null;
};

/**
 * 디버깅용: 현재 tenant 설정 상태 출력
 * 브라우저 콘솔에서 debugTenant() 호출하여 사용
 */
export const debugTenant = (): void => {
    const hostname = window.location.hostname;
    console.group("🔍 Tenant Debug Info (Learning Center B2B)");
    console.log("hostname:", hostname);
    console.log("port:", window.location.port);
    console.log("isLocalDevelopment:", isLocalDevelopment());
    console.log("baseDomain:", getBaseDomain(hostname));
    console.log("subdomain:", getSubdomain(hostname));
    console.log("isLandingPage:", isLandingPage());
    console.log("organizationType:", getOrganizationType());
    console.log("getTenantId():", getTenantId());
    console.log("localStorage.tenant:", localStorage.getItem("tenant"));
    console.log("DOMAIN_CONFIG:", DOMAIN_CONFIG);
    console.groupEnd();
};

// 개발 환경에서 전역으로 디버그 함수 노출
if (typeof window !== "undefined" && isLocalDevelopment()) {
    (window as unknown as Record<string, unknown>).debugTenant = debugTenant;
}

/**
 * Tenant ID 설정 (개발용)
 */
export const setTenantId = (tenantId: string): void => {
    localStorage.setItem("tenant", tenantId);
};

/**
 * Tenant ID 초기화
 */
export const clearTenantId = (): void => {
    localStorage.removeItem("tenant");
};

/**
 * CSS 변수로 테넌트 테마 적용
 */
export const applyTenantTheme = (primaryColor: string | null): void => {
    if (primaryColor) {
        document.documentElement.style.setProperty(
            "--tenant-primary-color",
            primaryColor
        );
        document.body.classList.add("tenant-theme");
    } else {
        document.documentElement.style.removeProperty("--tenant-primary-color");
        document.body.classList.remove("tenant-theme");
    }
};

/**
 * 테넌트 URL 생성
 * 개발: seoul101.localhost:3100
 * 프로덕션: seoul101.quebonlc.tv
 * 스테이징: seoul101.staging.quebonlc.tv
 */
export const getTenantUrl = (subdomain: string): string => {
    if (isLocalDevelopment()) {
        const hostname = window.location.hostname;
        if (hostname.endsWith(".nip.io")) {
            const base = getBaseDomain(hostname);
            const port = window.location.port || "3100";
            return `http://${subdomain}.${base}:${port}`;
        }
        return `http://${subdomain}.localhost:3100`;
    }
    const baseDomain = getBaseDomain(window.location.hostname) || "quebonlc.tv";
    return `https://${subdomain}.${baseDomain}`;
};

/**
 * 랜딩 페이지 URL 생성
 * 개발: localhost:3100
 * 프로덕션: quebonlc.tv
 * 스테이징: staging.quebonlc.tv
 */
export const getLandingUrl = (): string => {
    if (isLocalDevelopment()) {
        const hostname = window.location.hostname;
        if (hostname.endsWith(".nip.io")) {
            const base = getBaseDomain(hostname);
            const port = window.location.port || "3100";
            return `http://${base}:${port}`;
        }
        return `http://localhost:3100`;
    }
    const baseDomain = getBaseDomain(window.location.hostname) || "quebonlc.tv";
    return `https://${baseDomain}`;
};
